import { isBoolean, isString, type KeysOfValue } from "@mcwd/typescript-type-guards";
import { useFormLogger } from "./use-form-logger.js";
import { type ComputedRef, computed } from "vue";
type BaseCustomData = {
  formHeading?: string,
  secondFormHeading?: string,
  ctaButtonText?: string,
  CustomPostMessageHeader?: string,
  CustomPostMessageBody?: string
  hasPhoneValidation?: boolean,
  disableFormHeading?: boolean,
  multipleRowsLayout?: boolean,
  isLunchAndLearn?: boolean,
  eventType?: string,
};
type IntersectCustomData = BaseCustomData & { [key: string]: unknown };
export type CustomData = { [K in keyof IntersectCustomData]: IntersectCustomData[K] };
export type ComputedCustomData = { [K in keyof BaseCustomData]-?: ComputedRef<Required<IntersectCustomData>[K]> };

const CustomTextPossibleKeys = [
  "formHeading",
  "secondFormHeading",
  "ctaButtonText",
  "CustomPostMessageHeader",
  "CustomPostMessageBody",
  "eventType" ] as const satisfies KeysOfValue<Required<BaseCustomData>, string | null>[];
type CustomTextPossibleKeys = typeof CustomTextPossibleKeys[number];
export function getCustomFormText<T extends Record<CustomTextPossibleKeys | K, any> | undefined, K extends keyof any>(
  customData: T,
  customField: CustomTextPossibleKeys
): string | null {

  const { getVerboseLogger } = useFormLogger({ callerLabel: 'getCustomFormText' });
  const verboseLogger = getVerboseLogger("getCustomFormText");

  if (customData && (customField in customData)) {
    const customText: unknown = customData[customField];
    verboseLogger.debug(`Custom Form Text for field '${customField}': `, customText);
    if (isString(customText) && customText.trim().length > 0) {
      return customText;
    }
  }
  return null;
}
const CustomBooleanPossibleKeys = [
  "disableFormHeading",
  "hasPhoneValidation",
  "multipleRowsLayout",
  "isLunchAndLearn"] as const satisfies KeysOfValue<Required<BaseCustomData>, boolean>[];
type CustomBooleanPossibleKeys = typeof CustomBooleanPossibleKeys[number];

export function getCustomFormBoolean(
  customData: CustomData | undefined,
  customField: CustomBooleanPossibleKeys
): boolean {

  const { getVerboseLogger } = useFormLogger({ callerLabel: 'getCustomFormBoolean' });
  const verboseLogger = getVerboseLogger("getCustomFormBoolean");

  if (customData && (customField in customData)) {
    const customValue: unknown = customData[customField];
    verboseLogger.debug(`Custom Form Value for field '${customField}': `, customValue);
    if (isBoolean(customValue)) {
      return customValue;
    }
  }
  return false;
}


type AllCustomDataKeys = CustomTextPossibleKeys | CustomBooleanPossibleKeys;
//Generic function to retrieve both text and boolean 
export function getFormCustomDataValue(
  customData: CustomData | undefined,
  customField: CustomTextPossibleKeys,
): string | null;
export function getFormCustomDataValue(
  customData: CustomData | undefined,
  customField: CustomBooleanPossibleKeys,
): boolean;

export function getFormCustomDataValue(
  customData: CustomData | undefined,
  customField: AllCustomDataKeys,
): string | boolean | null {

  if (CustomTextPossibleKeys.includes(customField as CustomTextPossibleKeys)) {
    return getCustomFormText(customData, customField as CustomTextPossibleKeys);
  }
  else {
    return getCustomFormBoolean(customData, customField as CustomBooleanPossibleKeys);
  }
}

export function GetComputedProperties(customData: CustomData | undefined): ComputedCustomData {
  const ComputedCustomDataObj = {} as ComputedCustomData;

  const { getVerboseLogger } = useFormLogger({ callerLabel: 'GetComputedProperties' });
  const verboseLogger = getVerboseLogger("GetComputedProperties");

  if (customData) {
    //string based properties
    for (const field of CustomTextPossibleKeys) {
      verboseLogger.debug(`Custom Form Text for field '${field}': `, customData[field]);
      ComputedCustomDataObj[field] = computed(() => { return customData[field] ?? ""; });
    }
    //boolean based properties 
    for (const field of CustomBooleanPossibleKeys) {
      verboseLogger.debug(`Custom Form Boolean for field '${field}': `, customData[field]);
      ComputedCustomDataObj[field] = computed(() => { return customData[field] ?? false; });
    }
  }
  return ComputedCustomDataObj;
}